/**
 * StundenkontoUebersicht — Desktop-Version mit gleichem Layout wie das Mobile Stundenkonto.
 *
 * Ronny 2026-07-08: Desktop-Ansicht war zu tabellenlastig — neuer Aufbau spiegelt das
 * kompakte, klare Mobile-Design. Admins können zusätzlich Mitarbeiter auswählen.
 *
 * Die vollständige Tabellen-Ansicht bleibt unter /app/stundenkonten/tabelle erhalten.
 */
import { useState, useEffect } from "react";
import { authFetch, useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import { Users, Table2 } from "lucide-react";
import MobileStundenkonto from "./mobile/MobileStundenkonto";

const API = "/api";

export default function StundenkontoUebersicht() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = (user?.role || "").toLowerCase();
  const isPrivileged = role === "admin" || role === "projektleiter"
    || !!user?.permissions?.can_view_all_entries;

  const [employees, setEmployees] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => {
    if (!isPrivileged) return;
    let alive = true;
    (async () => {
      try {
        const r = await authFetch(`${API}/users`);
        if (!r.ok) return;
        const list = await r.json();
        if (alive) {
          setEmployees(
            (list || [])
              .filter(u => u.is_active !== false)
              .sort((a, b) => (a.name || a.email || "").localeCompare(b.name || b.email || ""))
          );
        }
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [isPrivileged]);

  const currentTargetName = selectedUserId
    ? (employees.find(u => u.id === selectedUserId)?.name || "Mitarbeiter")
    : (user?.name || "Ich");

  return (
    <div className="min-h-screen bg-[#0f1117] text-white">
      {/* Desktop-Header */}
      <div className="border-b border-white/8 bg-[#141721]">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              Stundenkonto
            </h1>
            <div className="text-sm text-gray-400 mt-0.5" data-testid="sk-current-target">
              {selectedUserId ? `Ansicht für: ${currentTargetName}` : "Deine Übersicht"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/app/stundenkonten/tabelle")}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-white/[0.04] border border-white/10 text-gray-300 hover:bg-white/[0.08] transition-colors"
              data-testid="sk-open-table-view"
              title="Detail-Tabelle mit allen Spalten"
            >
              <Table2 className="w-3.5 h-3.5" />
              Detail-Tabelle
            </button>
          </div>
        </div>
      </div>

      {/* Content — nutzt exakt das Mobile-Layout, in einem breiteren Container */}
      <MobileStundenkonto
        userIdOverride={selectedUserId}
        showEmployeeSelector={isPrivileged}
        employeeList={employees}
        onEmployeeChange={setSelectedUserId}
        containerClass="max-w-4xl mx-auto px-6 py-6 space-y-4"
      />
    </div>
  );
}
